import { CloseFullscreenRounded } from '@mui/icons-material'
import { IconButton, Stack, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridPaginationMeta, GridPaginationModel } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import { IPaginationQueryResponse } from '../../definitions/Common'
import { ICostUtilization } from '../../definitions/DataAnalytics'
import ContractsAnalyticsManager from '../../managers/ContractsAnalyticsManager'
import { useAppSelector } from '../../store/Hooks'
import { overwriteRouteParams } from '../../store/slices/SessionSlice'
import { SectionView } from '../contract_tabs/NewContractAnalytics'
import SmartCard from '../controls/cards/SmartCard'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import { IMenuItem, IRouteParameters } from '../routes/ContractDetails'
import RoutingManager from '../routing/Routing'
import { snackbar } from '../services/SnackbarService'

interface P {
    contractId: number
    filterStartDate?: string
    filterEndDate?: string
    changeView: (view: SectionView) => void
    updateDataFlag: boolean
    setExportMenu: (items: IMenuItem[]) => void
}

export const valueFormatter = (unit: 'percent' | 'currency' | 'decimal', value: number) => {
    return new Intl.NumberFormat('en-US',
        {
            style: unit,
            ...(unit === 'currency' && { currency: 'USD' }),
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value)
}

const costUtilizationConfig = ContractsAnalyticsManager.costUtilizationConfig

const columns: GridColDef<ICostUtilization>[] = [
    {
        field: 'horizon',
        headerName: costUtilizationConfig.horizon.name,
        flex: 1
    },
    {
        field: 'practiceGroup',
        headerName: costUtilizationConfig.practiceGroup.name,
        flex: 1.5
    },
    {
        field: 'ageGroup',
        headerName: costUtilizationConfig.ageGroup.name,
        flex: 1
    },
    {
        field: 'tosLevelOne',
        headerName: costUtilizationConfig.tosLevelOne.name,
        flex: 1
    },
    {
        field: 'tosLevelTwo',
        headerName: costUtilizationConfig.tosLevelTwo.name,
        flex: 1,
        renderCell: params => params?.value ?? 'missing'
    },
    {
        field: 'tosLevelThree',
        headerName: costUtilizationConfig.tosLevelThree.name,
        flex: 1,
        renderCell: params => params?.value ?? 'missing'
    },
    {
        field: 'entityTCOC',
        headerName: costUtilizationConfig.entityTCOC.name,
        flex: 1, type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'entityMemberMonths',
        headerName: costUtilizationConfig.entityMemberMonths.name,
        flex: 1, type: 'number'
    },
    {
        field: 'entityPMPMUnadjusted',
        headerName: costUtilizationConfig.entityPMPMUnadjusted.name,
        flex: 1, type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'networkPMPMUnadjusted',
        headerName: costUtilizationConfig.networkPMPMUnadjusted.name,
        flex: 1, type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'networkPMPMRisk',
        headerName: costUtilizationConfig.networkPMPMRisk.name,
        flex: 1, type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'diffNetworkEventsRisk',
        headerName: costUtilizationConfig.diffNetworkEventsRisk.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('percent', params.value)
    },
    {
        field: 'entityEvents',
        headerName: costUtilizationConfig.entityEvents.name,
        flex: 1,
        type: 'number'
    },
    {
        field: 'entityEventsDiv1000Unadjusted',
        headerName: costUtilizationConfig.entityEventsDiv1000Unadjusted.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('decimal', params.value)
    },
    {
        field: 'networkEventsDiv1000Unadjusted',
        headerName: costUtilizationConfig.networkEventsDiv1000Unadjusted.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('decimal', params.value)
    },
    {
        field: 'networkEventsDiv1000Adjusted',
        headerName: costUtilizationConfig.networkEventsDiv1000Adjusted.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('decimal', params.value)
    },
    {
        field: 'diffNetworkEventsDiv1000Adjusted',
        headerName: costUtilizationConfig.diffNetworkEventsDiv1000Adjusted.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('percent', params.value)
    },
    {
        field: 'entityTCOCDivEvents',
        headerName: costUtilizationConfig.entityTCOCDivEvents.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'networkTCOCDivEntityEvents',
        headerName: costUtilizationConfig.networkTCOCDivEntityEvents.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'diffNetworkTCOCDivEntityEvents',
        headerName: costUtilizationConfig.diffNetworkTCOCDivEntityEvents.name,
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('percent', params.value)
    },
    {
        field: 'trendEntityPMPM',
        headerName: costUtilizationConfig.trendEntityPMPM.name,
        flex: 1,
        type: 'number'
    },
    {
        field: 'trendUnadjustedNetworkPMPM',
        headerName: costUtilizationConfig.trendUnadjustedNetworkPMPM.name,
        flex: 1,
        type: 'number'
    },
    {
        field: 'trendEntityEventsDiv1000',
        headerName: costUtilizationConfig.trendEntityEventsDiv1000.name,
        flex: 1,
        type: 'number'
    },
    {
        field: 'trendUnadjustedNetworkEventsDiv1000',
        headerName: costUtilizationConfig.trendUnadjustedNetworkEventsDiv1000.name,
        flex: 1,
        type: 'number'
    }
]

const slotProps: any = {
    loadingOverlay: {
        variant: 'skeleton'
    }
}

const sx = {
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: theme => theme.palette.background.paper
    },
    'borderStyle': 'none',
    '&.MuiDataGrid-root': {
        paddingTop: 3,
        borderRadius: 2.5
    }, '& .MuiDataGrid-cell:focus': {
        outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    }
}

const PAGE_SIZE = 20

const skeletonRows = Array.from({ length: 10 }, (_, i) => ({
    uuid: `skeleton_row_${i}`,
    practiceGroup: 'string',
    horizon: 'string',
    ageGroup: 'string',
    tosLevelOne: 'string',
    tosLevelTwo: 'string',
    tosLevelThree: 'string',
    entityTCOC: 666,
    entityMemberMonths: 666,
    networkMemberMonths: 666,
    entityPMPMUnadjusted: 666,
    networkPMPMUnadjusted: 666,
    networkPMPMRisk: 666,
    diffNetworkEventsRisk: 666,
    entityEvents: 666,
    entityEventsDiv1000Unadjusted: 666,
    networkEventsDiv1000Unadjusted: 666,
    entityTCOCDivEvents: 666,
    networkEventsDiv1000Adjusted: 666,
    networkTCOCDivEntityEvents: 666,
    diffNetworkTCOCDivEntityEvents: 666,
    diffNetworkEventsDiv1000Adjusted: 666,
    trendEntityPMPM: 666,
    trendUnadjustedNetworkPMPM: 666,
    trendEntityEventsDiv1000: 666,
    trendUnadjustedNetworkEventsDiv1000: 666
}))

const CostUtilizationView = ({ contractId, filterStartDate, filterEndDate, changeView, updateDataFlag, setExportMenu }: P) => {
    const dispatch = useDispatch()
    const params = RoutingManager.getParams<IRouteParameters>(useAppSelector(s => s.session.routeParams))
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZE })
    const [rowCountState, setRowCountState] = useState(0)
    const [offset, setOffset] = useState(0)
    const paginationMetaRef = useRef<GridPaginationMeta>(undefined)

    const { response: responseItems, status: responseStatus } = useQuery<IPaginationQueryResponse<ICostUtilization>>({
        queryKey: buildQueryKey(
            'contractCostUtilization',
            params?.contractId ?? contractId,
            params?.startDate ?? filterStartDate,
            params?.endDate ?? filterEndDate,
            paginationModel.page === 0 ? 'first' : paginationModel.page
        ),
        queryFn: async () => {
            const result = await ApiClient.getContractCostUtilizationByPeriod({
                contractId: params?.contractId ?? contractId,
                startDate: params?.startDate ?? filterStartDate,
                endDate: params?.endDate ?? filterEndDate,
                limit: params?.limit ?? PAGE_SIZE,
                offset: params?.offset ?? offset
            })
            if (result?.items?.length > 0) setOffset(prev => prev + (params?.limit ?? PAGE_SIZE))
            return result
        },
        cache: true
    })
    const isLoading = responseStatus === 'loading'

    const paginationMeta = useMemo(() => {
        if (responseItems?.hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== responseItems?.hasNextPage) {
            paginationMetaRef.current = { hasNextPage: responseItems?.hasNextPage }
        }
        return paginationMetaRef.current
    }, [responseItems?.hasNextPage])

    const rows = useMemo(() => responseItems?.items ?? [], [responseItems?.items])

    useEffect(() => {
        if (!paginationMeta?.hasNextPage) {
            setRowCountState(paginationModel.page * (params?.limit ?? PAGE_SIZE) + (responseItems?.items?.length ?? 0))
        } else {
            setRowCountState(-1)
        }
    }, [paginationMeta?.hasNextPage, paginationModel.page, responseItems?.items])

    useEffect(() => {
        setOffset(0)
        setPaginationModel({ page: 0, pageSize: params?.limit ?? PAGE_SIZE })
    }, [updateDataFlag])

    useEffect(() => {
        setExportMenu([
            { fileType: 'csv', dataSource: 'costUtilization', dataViewMode: params?.dataViewMode ?? 'pmpm' },
            { fileType: 'link' }
        ])
    }, [])

    const handleChangeView = () => {
        changeView('main')
    }

    const handlePaginationModelChange = useCallback((newPaginationModel: GridPaginationModel) => {
        setOffset(newPaginationModel.page * (params?.limit ?? PAGE_SIZE))
        setPaginationModel(newPaginationModel)
    }, [])

    if (responseStatus === 'error') snackbar.show({ severity: 'error', text: 'Error during data fetch, try refreshing the page' })

    return (
        <>
            <Tile.Container>
                <Tile.Column>
                    <SmartCard>
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <Stack>
                                <Typography variant='h6'>
                                    Cost utilization
                                </Typography>
                                <Typography variant='body2'>
                                    March
                                </Typography>
                            </Stack>
                            <IconButton
                                onClick={() => {
                                    handleChangeView()
                                    dispatch(overwriteRouteParams({ ...params, analyticsSection: 'main' }))
                                }}
                                size='small'>
                                <CloseFullscreenRounded />
                            </IconButton>
                        </Stack>
                        <DataGrid
                            sx={sx}
                            loading={isLoading}
                            columns={columns}
                            getRowId={row => row.uuid}
                            pageSizeOptions={[params?.limit ?? PAGE_SIZE]}
                            rows={isLoading ? skeletonRows : rows}
                            rowCount={rowCountState}
                            paginationMode='server'
                            paginationMeta={paginationMeta}
                            paginationModel={paginationModel}
                            onPaginationModelChange={handlePaginationModelChange}
                            slotProps={slotProps}
                            autoHeight
                            disableRowSelectionOnClick
                            autosizeOnMount
                        />
                    </SmartCard>
                </Tile.Column>
            </Tile.Container>
        </>
    )
}

export default CostUtilizationView
